<template>
	<button
		v-if="isCollapsed"
		type="button"
		class="question-entry"
		:aria-label="t('展开常见问题')"
		@click="toggleCollapse"
	>
		<span>{{ t("常见问题") }}</span>
	</button>
	<div v-else class="question-panel">
		<div class="question-header">
			<p class="question-title">{{ t("常见问题") }}</p>
			<button
				type="button"
				class="question-toggle"
				:aria-label="t('收起常见问题')"
				@click="toggleCollapse"
			>
				<img :src="faqCollapseBack" class="question-toggle__icon" width="24" height="24" alt="" />
			</button>
		</div>
		<div class="question-list">
			<button
				v-for="(q, i) in questions"
				:key="i"
				type="button"
				class="question-chip"
				@click="$emit('send', q, i)"
			>
				{{ q }}
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { getQuestionList } from "../../../../../../api/master-s";
import { faqCollapseBack } from "@variants/seer/master-s/shared/images";
import { t } from "@variants/seer/master-s/shared/utils/i18n";

const props = defineProps<{
	type: number;
	collapsed?: boolean;
}>();
const emit = defineEmits<{
	send: [text: string, position: number];
	"update:collapsed": [value: boolean];
}>();

const questions = ref<string[]>([]);
const isCollapsed = computed({
	get: () => Boolean(props.collapsed),
	set: (value: boolean) => emit("update:collapsed", value),
});

async function loadQuestions() {
	const type = Number(props.type || 0);
	if (!type) {
		questions.value = [];
		return;
	}
	try {
		const list = await getQuestionList(type);
		questions.value = Array.isArray(list) ? list.map(item => String(item)) : [];
	} catch (e) {
		console.error("getQuestionList failed", e);
		questions.value = [];
	}
}

function toggleCollapse() {
	isCollapsed.value = !isCollapsed.value;
}

watch(
	() => props.type,
	() => {
		void loadQuestions();
	},
	{ immediate: true }
);
</script>

<style scoped>
/* Figma 塔罗/六爻底部常见问题区 1299:32427 / 1299:32449：309×74 → 750稿 ×2 */
.question-panel {
	width: 100%;
	box-sizing: border-box;
	padding: 24px 0 0;
	border: 2px solid #fff;
	border-radius: 20px;
	background: linear-gradient(180deg, #fff8e9 0%, #fff 100%);
	overflow: hidden;
}

.question-title {
	margin: 0;
	padding: 0;
	font-size: 28px;
	font-weight: 400;
	line-height: 1.2;
	color: #333;
}

.question-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 16px;
	padding: 0 26px;
}

.question-toggle {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	padding: 0;
	border: 0;
	background: transparent;
	cursor: pointer;
}

.question-toggle__icon {
	display: block;
	width: 24px;
	height: 24px;
	object-fit: contain;
	transform: rotate(90deg);
}

.question-list {
	display: flex;
	flex-wrap: nowrap;
	gap: 16px;
	align-items: center;
	overflow-x: auto;
	padding: 0 26px 24px;
	-webkit-overflow-scrolling: touch;
}

.question-entry {
	display: inline-flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	height: 56px;
	padding: 0 16px;
	border: 2px solid #efefef;
	border-radius: 61px;
	background: rgba(255, 255, 255, 0.7);
	color: #333;
	font-size: 24px;
	font-weight: 400;
	line-height: 1;
	white-space: nowrap;
	cursor: pointer;
	box-sizing: border-box;
}

.question-chip {
	display: inline-flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	padding: 16px;
	border: 1.63px solid #fff4dd;
	border-radius: 122px;
	background: rgba(255, 255, 255, 0.7);
	color: #333;
	font-size: 24px;
	font-weight: 400;
	line-height: 20px;
	white-space: nowrap;
	box-sizing: border-box;
}
</style>
